'use strict';

/**
 * B2/B4 JIT custody — the ONE shared `Authorization: JIT` fetch wrapper.
 *
 * Every gated-prefix call site (tasks / calendar / mail / vault-blob — the
 * auth/jit-content-gate.js CONTENT_GATE_PREFIXES) routes through this wrapper
 * instead of growing its own copy of the mint+header logic.
 *
 * CHALLENGE-RESPONSE, ZERO-PROBE DESIGN (why there is no flag endpoint):
 * while JIT_GRANT_ENFORCEMENT is OFF the mounted gate is a pure pass-through
 * and never emits a JIT_* code — so this wrapper never activates and the
 * flag-OFF wire traffic is byte-for-byte identical to a bare fetch (one
 * request, same args, no header). The FIRST 400/401/403/409/410 carrying a
 * JIT_* code is the server's own signal that enforcement is ON: the wrapper
 * mints a fresh single-use grant with the session signer, retries ONCE, and
 * remembers ("sticky") so later requests pre-mint in a single round-trip.
 * Grants are single-use (jti) — every request mints fresh; there is nothing
 * to cache and no token ever touches storage.
 *
 * FAIL-OPEN TO THE REFUSAL, NEVER A LOOP: no signer / no userId / stream body
 * / mint failure ⇒ the original response passes through untouched. The
 * wrapper can only ever ADD the header the gate demands; it can never bypass
 * the gate's identity binding (grant.userId must equal the authenticated
 * account — enforced server-side) and never retries more than once.
 *
 * Pure + injectable (fetchImpl / resolveSigner / resolveUserId) — Node-testable;
 * the browser default instance is wired in browser-bundle-entry.js.
 */

const { mintJitGrant, encodeJitGrantToken } = require('./jit-grant-mint.js');

// The middleware statuses that can carry a JIT_* refusal (auth/require-jit-grant.js).
// 500 (JIT_VALIDATOR_ERROR) is deliberately excluded: retrying an unrelated 500
// could double a non-idempotent write.
const JIT_CHALLENGE_STATUSES = { 400: true, 401: true, 403: true, 409: true, 410: true };

function pathnameOf(input) {
  try {
    const url = typeof input === 'string' ? input : String((input && input.url) || '');
    // Placeholder base resolves the relative form used by every call site.
    return new URL(url, 'http://oyster.invalid').pathname;
  } catch (_e) {
    return null;
  }
}

// Action-bound verb (T568 LD-9): folded into the SIGNED grant body so a read
// grant can never be escalated post-signing. Optional at the content gate
// today — inert hardening + better ledger forensics.
function verbForMethod(init) {
  const m = String((init && init.method) || 'GET').toUpperCase();
  if (m === 'GET' || m === 'HEAD' || m === 'OPTIONS') return 'read';
  if (m === 'DELETE') return 'delete';
  return 'write';
}

// Only replayable bodies may be re-sent on the retry; a consumed stream must not.
function isReplayableBody(init) {
  const b = init && init.body;
  if (b == null) return true;
  if (typeof b === 'string') return true;
  if (b instanceof Uint8Array || (typeof ArrayBuffer !== 'undefined' && b instanceof ArrayBuffer)) return true;
  if (typeof URLSearchParams !== 'undefined' && b instanceof URLSearchParams) return true;
  if (typeof Blob !== 'undefined' && b instanceof Blob) return true;
  if (typeof FormData !== 'undefined' && b instanceof FormData) return true;
  return false;
}

// New init with the Authorization header set — the caller's init is never mutated.
function withAuthHeader(init, headerValue) {
  const out = Object.assign({}, init || {});
  if (typeof Headers !== 'undefined' && out.headers instanceof Headers) {
    const h = new Headers(out.headers);
    h.set('Authorization', headerValue);
    out.headers = h;
  } else if (Array.isArray(out.headers)) {
    out.headers = out.headers.concat([['Authorization', headerValue]]);
  } else {
    out.headers = Object.assign({}, out.headers || {}, { Authorization: headerValue });
  }
  return out;
}

/**
 * @param {object} opts
 * @param {Function} [opts.fetchImpl] — underlying fetch (defaults to global fetch,
 *   bound so browser invocation is legal).
 * @param {() => (object|null)} opts.resolveSigner — returns the session's
 *   sign-only signer ({ sign, publicKey } — shared/crypto/jit-signer.js) or null.
 * @param {() => (string|null)} opts.resolveUserId — returns the authenticated
 *   account identifier the grant must be issued for, or null.
 * @returns {(input: string|Request, init?: object) => Promise<Response>}
 */
function createJitFetch({ fetchImpl, resolveSigner, resolveUserId } = {}) {
  const f = fetchImpl
    || (typeof fetch === 'function' ? fetch.bind(typeof globalThis !== 'undefined' ? globalThis : undefined) : null);
  if (typeof f !== 'function') throw new Error('createJitFetch: a fetch implementation is required');
  if (typeof resolveSigner !== 'function') throw new Error('createJitFetch: resolveSigner is required');
  if (typeof resolveUserId !== 'function') throw new Error('createJitFetch: resolveUserId is required');

  // Page-lifetime sticky bit: set the first time the server demands a grant.
  let enforcementSeen = false;

  async function mintHeaderFor(input, init) {
    const signer = resolveSigner();
    if (!signer || typeof signer.sign !== 'function') return null;
    const userId = resolveUserId();
    if (userId == null || userId === '') return null;
    const dataPath = pathnameOf(input);
    if (!dataPath) return null;
    const grant = await mintJitGrant({
      signer, userId: String(userId), dataPath, verb: verbForMethod(init),
    });
    return 'JIT ' + encodeJitGrantToken(grant);
  }

  return async function jitFetch(input, init) {
    // Sticky pre-mint: after enforcement has been observed once on this page,
    // carry the grant up front (single round-trip). Mint failure falls back to
    // the bare request — the server refusal then tells the truth.
    if (enforcementSeen) {
      let header = null;
      try { header = await mintHeaderFor(input, init); } catch (_e) { /* bare request below */ }
      if (header) return f(input, withAuthHeader(init, header));
    }

    const res = await f(input, init);
    if (!res || !JIT_CHALLENGE_STATUSES[res.status]) return res;

    // Peek the refusal code on a CLONE — the caller's body stays readable.
    let code = null;
    try {
      const peek = await res.clone().json();
      code = peek && peek.code;
    } catch (_e) {
      return res; // non-JSON error body — not the gate's refusal shape
    }
    if (typeof code !== 'string' || code.indexOf('JIT_') !== 0) return res;

    enforcementSeen = true;

    // Retry ONCE, only when the request is faithfully replayable.
    if (typeof input !== 'string' || !isReplayableBody(init)) return res;
    let header = null;
    try { header = await mintHeaderFor(input, init); } catch (_e) { return res; }
    if (!header) return res;
    return f(input, withAuthHeader(init, header));
  };
}

module.exports = { createJitFetch, JIT_CHALLENGE_STATUSES };
